import { z } from "zod";

// --- Paths ---

export const guestSessionPath = "/api/guest/session";
export const guestSessionRefreshPath = "/api/guest/session/refresh";
export const guestMergePath = "/api/guest/merge";

// --- Request / response schemas ---

export const createGuestSessionBodySchema = z.object({
  token: z.string().min(1),
  browser_id: z.string().min(1),
  display_name: z.string().max(100).optional(),
});

export const createGuestSessionSuccessSchema = z.object({
  session_id: z.string(),
  guest_user_id: z.string(),
  channel_id: z.string(),
  zero_token: z.string(),
  expires_at: z.number(),
  invited_email: z.string().nullish(),
  inviter_name: z.string().nullish(),
  inviter_avatar_url: z.string().nullish(),
});

export const refreshGuestTokenBodySchema = z.object({
  session_id: z.string().min(1),
  browser_id: z.string().min(1),
});

export const refreshGuestTokenSuccessSchema = z.object({
  zero_token: z.string(),
  expires_at: z.number(),
});

export const mergeGuestAccountBodySchema = z.object({
  session_id: z.string().min(1),
  browser_id: z.string().min(1),
});

export const mergeGuestAccountSuccessSchema = z.object({
  merged: z.literal(true),
  channel_id: z.string(),
});

const guestErrorSchema = z.object({
  error: z.string(),
});

// --- Types ---

export type CreateGuestSessionBody = z.infer<
  typeof createGuestSessionBodySchema
>;
export type CreateGuestSessionSuccess = z.infer<
  typeof createGuestSessionSuccessSchema
>;
export type RefreshGuestTokenBody = z.infer<typeof refreshGuestTokenBodySchema>;
export type RefreshGuestTokenSuccess = z.infer<
  typeof refreshGuestTokenSuccessSchema
>;
export type MergeGuestAccountBody = z.infer<typeof mergeGuestAccountBodySchema>;
export type MergeGuestAccountSuccess = z.infer<
  typeof mergeGuestAccountSuccessSchema
>;

// --- Request helpers ---

import { normalizeApiBaseUrl, type FetchLike } from "./shared";

export class GuestApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "GuestApiError";
  }
}

async function parseError(response: Response): Promise<GuestApiError> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = guestErrorSchema.safeParse(payload);
  return new GuestApiError(
    parsed.success ? parsed.data.error : "Request failed",
    response.status,
  );
}

export async function createGuestSessionRequest({
  apiBaseUrl,
  token,
  browserId,
  displayName,
  fetchImpl,
}: {
  apiBaseUrl: string;
  token: string;
  browserId: string;
  displayName?: string;
  fetchImpl?: FetchLike;
}): Promise<CreateGuestSessionSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${guestSessionPath}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        browser_id: browserId,
        display_name: displayName,
      }),
    },
  );
  if (!response.ok) throw await parseError(response);
  const payload = await response.json();
  const parsed = createGuestSessionSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new GuestApiError("Invalid guest session response", 500);
  }
  return parsed.data;
}

export async function refreshGuestTokenRequest({
  apiBaseUrl,
  sessionId,
  browserId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  sessionId: string;
  browserId: string;
  fetchImpl?: FetchLike;
}): Promise<RefreshGuestTokenSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${guestSessionRefreshPath}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId, browser_id: browserId }),
    },
  );
  if (!response.ok) throw await parseError(response);
  const payload = await response.json();
  const parsed = refreshGuestTokenSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new GuestApiError("Invalid guest token refresh response", 500);
  }
  return parsed.data;
}

export async function mergeGuestAccountRequest({
  apiBaseUrl,
  sessionId,
  browserId,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  sessionId: string;
  browserId: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<MergeGuestAccountSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${guestMergePath}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ session_id: sessionId, browser_id: browserId }),
    },
  );
  if (!response.ok) throw await parseError(response);
  const payload = await response.json();
  const parsed = mergeGuestAccountSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new GuestApiError("Invalid merge response", 500);
  }
  return parsed.data;
}
